import React, { memo, useCallback } from 'react';

import { Box, TableSortLabel, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import ColumnFilterElement from '../ColumnFilterElement';

import SortIcon from './SortIcon';
import { SORT_DIRECTIONS, SORT_LABEL_ALIGNMENT } from './TooltipedSortLabel.constants';
import {
  getFilterWrapperStyles,
  getIconWrapperStyles,
  getWrapperStyles,
  labelStyles,
} from './TooltipedSortLabel.styles';

const defaultObject = {};

const TooltipedSortLabel = ({
  isSort = false,
  id,
  label,
  order = defaultObject,
  onRequestSort,
  align = SORT_LABEL_ALIGNMENT.LEFT,
  disabled = false,
  filter = null,
  columnKey = null,
  columnFilters = null,
  onColumnFiltersChange = null,
  ...props
}) => {
  const SortIconComponent = useCallback(() => {
    const isInitial = order.id !== id;

    return <SortIcon direction={isInitial ? SORT_DIRECTIONS.NONE : order.direction} />;
  }, [order, id]);

  const hasFilter = !!filter && !!onColumnFiltersChange;

  const content = (
    <Typography data-truncated className="truncate" sx={labelStyles} variant="regular14">
      {label}
    </Typography>
  );

  const filterElement = hasFilter ? (
    <ColumnFilterElement
      columnFilters={columnFilters}
      columnKey={columnKey}
      filter={filter}
      onColumnFiltersChange={onColumnFiltersChange}
    />
  ) : null;

  if (!isSort) {
    return (
      <Box sx={hasFilter ? getFilterWrapperStyles(align) : getWrapperStyles(align)}>
        {content}
        {filterElement}
      </Box>
    );
  }

  return (
    <TableSortLabel
      {...props}
      hideSortIcon
      className="text-center"
      data-id={id}
      direction={order.direction}
      sx={hasFilter ? getFilterWrapperStyles(align) : getWrapperStyles(align)}
      onClick={disabled ? undefined : onRequestSort}
    >
      {content}
      <Box
        className="sort-icon-wrapper"
        sx={getIconWrapperStyles(order.id === id && order.direction)}
      >
        <SortIconComponent />
      </Box>
      {filterElement}
    </TableSortLabel>
  );
};

TooltipedSortLabel.propTypes = {
  id: PropTypes.string.isRequired,
  align: PropTypes.oneOf(Object.values(SORT_LABEL_ALIGNMENT)),
  isSort: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.node,
  order: PropTypes.object,
  filter: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  columnKey: PropTypes.string,
  columnFilters: PropTypes.object,
  onRequestSort: PropTypes.func,
  onColumnFiltersChange: PropTypes.func,
};

export default memo(TooltipedSortLabel);
